// src/components/AsksDataComponent.jsx
import React, { useState, useEffect } from 'react';
import { fetchAsksData, fetchAsksDataWithHeaders, postAskData, updateAskData, deleteAskData } from '../services/asksService';
import { isAuthenticated, updateAuthToken } from '../utils/api';

const AsksDataComponent = ({ onAuthRequired, showActions = false }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAuth, setIsAuth] = useState(isAuthenticated());
    const [newAskTitle, setNewAskTitle] = useState('');
    const [newAskDescription, setNewAskDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        // Check authentication status
        setIsAuth(isAuthenticated());
        
        // Listen for auth changes
        const handleAuthUpdate = () => {
            setIsAuth(isAuthenticated());
        };
        
        window.addEventListener('auth:token-updated', handleAuthUpdate);
        window.addEventListener('auth:unauthorized', () => {
            setIsAuth(false);
            setError('Session expired. Please log in again.');
            if (onAuthRequired) onAuthRequired();
        });
        
        return () => {
            window.removeEventListener('auth:token-updated', handleAuthUpdate);
            window.removeEventListener('auth:unauthorized', handleAuthUpdate);
        };
    }, [onAuthRequired]);

    useEffect(() => {
        if (isAuth) {
            loadData();
        } else {
            setLoading(false);
            setError('Authentication required. Please log in to view asks data.');
        }
    }, [isAuth]);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        
        const result = await fetchAsksData();
        console.log("TAQ", result)
        if (result.success) {
            setData(result.data);
        } else {
            setError(result.error);
            
            // Handle authentication errors
            if (result.status === 401 && onAuthRequired) {
                onAuthRequired();
            }
        }
        
        setLoading(false);
    };

    const handleRefresh = () => {
        if (isAuth) {
            loadData();
        }
    };

    const handleRetry = () => {
        if (isAuth) {
            loadData();
        } else if (onAuthRequired) {
            onAuthRequired();
        }
    };

    const handleCreateAsk = async (e) => {
        e.preventDefault();
        if (!newAskTitle.trim()) {
            setError('Please enter a title for the ask');
            return;
        }
        
        setIsSubmitting(true);
        setError(null);
        
        const result = await postAskData({
            title: newAskTitle,
            description: newAskDescription,
            createdAt: new Date().toISOString()
        });
        
        if (result.success) {
            setNewAskTitle('');
            setNewAskDescription('');
            await loadData(); // Refresh the list
        } else {
            setError(result.error);
        }
        
        setIsSubmitting(false);
    };

    const handleUpdateAsk = async (askId, updateData) => {
        const result = await updateAskData(askId, updateData);
        if (result.success) {
            await loadData(); // Refresh the list
            return true;
        } else {
            setError(result.error);
            return false;
        }
    };

    const handleDeleteAsk = async (askId) => {
        if (window.confirm('Are you sure you want to delete this ask?')) {
            const result = await deleteAskData(askId);
            if (result.success) {
                await loadData(); // Refresh the list
            } else {
                setError(result.error);
            }
        }
    };

    if (!isAuth) {
        return (
            <div className="asks-auth-required">
                <div className="auth-message">
                    <h3>Authentication Required</h3>
                    <p>Please log in to view asks data.</p>
                    <button onClick={handleRetry} className="login-button">
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="asks-loading">
                <div className="spinner"></div>
                <p>Loading asks data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="asks-error">
                <h3>Error Loading Data</h3>
                <p>{error}</p>
                <button onClick={handleRefresh} className="retry-button">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="asks-container">
            <div className="asks-header">
                <h2>Asks Data</h2>
                <button onClick={handleRefresh} className="refresh-button">
                    🔄 Refresh
                </button>
            </div>
            
            {showActions && (
                <div className="create-ask-form">
                    <h3>Create New Ask</h3>
                    <form onSubmit={handleCreateAsk}>
                        <input
                            type="text"
                            placeholder="Ask title"
                            value={newAskTitle}
                            onChange={(e) => setNewAskTitle(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <textarea
                            placeholder="Ask description (optional)"
                            value={newAskDescription}
                            onChange={(e) => setNewAskDescription(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creating...' : 'Create Ask'}
                        </button>
                    </form>
                </div>
            )}
            
            <DisplayAsksData 
                data={data} 
                onUpdate={showActions ? handleUpdateAsk : null}
                onDelete={showActions ? handleDeleteAsk : null}
            />
        </div>
    );
};

// Updated DisplayAsksData component with action handlers
const DisplayAsksData = ({ data, onUpdate, onDelete }) => {
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});

    const handleEdit = (ask) => {
        setEditingId(ask.id);
        setEditData({
            title: ask.title || '',
            description: ask.description || '',
            status: ask.status || ''
        });
    };

    const handleSave = async (askId) => {
        if (onUpdate) {
            const success = await onUpdate(askId, editData);
            if (success) {
                setEditingId(null);
                setEditData({});
            }
        }
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    if (Array.isArray(data)) {
        if (data.length === 0) {
            return <p className="no-data">No asks data available</p>;
        }
        
        return (
            <div className="asks-list">
                {data.map((item, index) => (
                    <AskCard 
                        key={item.id || index} 
                        ask={item}
                        isEditing={editingId === item.id}
                        editData={editData}
                        onEditChange={setEditData}
                        onSave={() => handleSave(item.id)}
                        onCancel={handleCancel}
                        onEdit={() => handleEdit(item)}
                        onDelete={onDelete ? () => onDelete(item.id) : null}
                    />
                ))}
            </div>
        );
    }
    
    if (typeof data === 'object' && data !== null) {
        return (
            <div className="asks-object">
                <pre className="json-display">
                    {JSON.stringify(data, null, 2)}
                </pre>
            </div>
        );
    }
    
    return (
        <div className="asks-primitive">
            <p>{String(data)}</p>
        </div>
    );
};

// Updated AskCard component with edit and delete actions
const AskCard = ({ ask, isEditing, editData, onEditChange, onSave, onCancel, onEdit, onDelete }) => {
    if (isEditing) {
        return (
            <div className="ask-card edit-mode">
                <div className="ask-card-header">
                    <input
                        type="text"
                        value={editData.title || ''}
                        onChange={(e) => onEditChange({ ...editData, title: e.target.value })}
                        placeholder="Title"
                    />
                </div>
                <div className="ask-card-body">
                    <textarea
                        value={editData.description || ''}
                        onChange={(e) => onEditChange({ ...editData, description: e.target.value })}
                        placeholder="Description"
                    />
                    <input
                        type="text"
                        value={editData.status || ''}
                        onChange={(e) => onEditChange({ ...editData, status: e.target.value })}
                        placeholder="Status"
                    />
                </div>
                <div className="ask-card-actions">
                    <button onClick={onSave} className="save-button">Save</button>
                    <button onClick={onCancel} className="cancel-button">Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div className="ask-card">
            <div className="ask-card-header">
                <h3>{ask.title || ask.name || `Ask #${ask.id}`}</h3>
                {ask.status && <span className={`status ${ask.status}`}>{ask.status}</span>}
            </div>
            
            <div className="ask-card-body">
                {ask.description && <p className="description">{ask.description}</p>}
                {ask.price && <p className="price">💰 {ask.price}</p>}
                {ask.createdAt && <p className="date">📅 {new Date(ask.createdAt).toLocaleDateString()}</p>}
            </div>
            
            {ask.tags && ask.tags.length > 0 && (
                <div className="ask-card-tags">
                    {ask.tags.map((tag, idx) => (
                        <span key={idx} className="tag">#{tag}</span>
                    ))}
                </div>
            )}

            {(onEdit || onDelete) && (
                <div className="ask-card-footer">
                    {onEdit && (
                        <button onClick={onEdit} className="edit-button">
                            ✏️ Edit
                        </button>
                    )}
                    {onDelete && (
                        <button onClick={onDelete} className="delete-button">
                            🗑️ Delete
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AsksDataComponent;